import config from './config';
import { appLocalStorage } from './storage';
import { getUser } from './func';
import { getSessionIdentity } from '../hooks/useSessionIdentity';
import { isDeletedOwner } from '../accountDeletion/ownerState';
import { abortable } from './abortable';
import { readWithRecovery } from './loadWithRecovery';

// Only the configured API may receive a session credential. Keep the canonical
// source (including its non-secret reaction hint) in HTML and local drafts;
// the fetched Blob is a temporary display resource, never stored editor data.
export function accountPhotoUrl(value, api = process.env.REACT_APP_API_URL) {
  if (typeof value !== 'string' || !value || !api) return null;
  try {
    const rawPath = value.split(/[?#]/, 1)[0];
    if (/\\|%(?:2f|5c)/i.test(rawPath)
      || /(?:^|\/)(?:\.|%2e){1,2}(?:\/|$)/i.test(rawPath)) return null;
    const base = new URL(api, window.location.origin);
    const source = new URL(value, `${base.origin}/`);
    const pathname = decodeURIComponent(source.pathname);
    if (!/^https?:$/.test(base.protocol) || base.username || base.password || source.origin !== base.origin
      || source.username || source.password
      // Legacy uploads kept human filenames. Match the server's safe basename
      // set after decoding, while excluding escapes, separators and controls.
      || !/^\/img\/(?:[a-zA-Z0-9_-]+\/)+[\p{L}\p{N}][\p{L}\p{M}\p{N} _().-]*\.(?:jpe?g|png|webp|gif|avif)$/iu.test(pathname)) return null;
    return source.href;
  } catch { return null; }
}

const cancelled = () => new DOMException('Photo request cancelled', 'AbortError');
const sessionChanged = () => Object.assign(new Error('PHOTO_SESSION_CHANGED'), { status: 401 });

export function readAccountPhoto(source, { signal, identity = getSessionIdentity() } = {}) {
  const url = accountPhotoUrl(source);
  if (!url) return Promise.reject(new Error('PHOTO_SOURCE_INVALID'));
  const token = appLocalStorage.getItem(config.token);
  const current = () => getSessionIdentity() === identity && !isDeletedOwner(getUser()?.id);
  return readWithRecovery(async ({ signal: cycleSignal, timeout }) => {
    if (!current()) throw sessionChanged();
    if (cycleSignal.aborted) throw cancelled();
    const controller = new AbortController();
    const abort = () => controller.abort();
    cycleSignal.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, timeout);
    try {
      const response = await abortable(fetch(url, {
        signal: controller.signal, credentials: 'omit', redirect: 'error', cache: 'no-store',
        headers: token ? { [config.token]: token } : {},
      }), controller.signal, cancelled());
      if (!current()) throw sessionChanged();
      if (!response.ok) throw Object.assign(new Error('PHOTO_UNAVAILABLE'), { status: response.status });
      const blob = await abortable(response.blob(), controller.signal, cancelled());
      if (!current()) throw sessionChanged();
      if (controller.signal.aborted) throw cancelled();
      if (!blob?.size || !/^image\/(?:jpeg|png|webp|gif|avif)(?:;|$)/i.test(blob.type)) {
        throw Object.assign(new Error('PHOTO_INVALID_RESPONSE'), { status: 502 });
      }
      return blob;
    } finally {
      clearTimeout(timer);
      cycleSignal.removeEventListener('abort', abort);
    }
  }, { signal });
}
